import type { ClaimOwnershipParams } from '@/services/tagService'

/**
 * OAuth on mobile browsers must use signInWithRedirect, not signInWithPopup:
 * iOS Safari blocks popups (and any await before the popup breaks the
 * user-gesture requirement, so "nothing happens"), and mobile Chrome loses the
 * popup result to storage partitioning ("back to the login page after the OS
 * prompt"). Redirect navigates the whole page away and back, so the state
 * needed to FINISH the flow on return is stashed in sessionStorage (per-origin,
 * survives the round-trip through the provider).
 */
export const isMobileBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false
  if (/iphone|ipad|ipod|android/i.test(navigator.userAgent)) return true
  // iPadOS 13+ Safari reports a desktop "Macintosh" UA by default; without this
  // iPad users would take the popup path, which iOS can block. A touch-capable
  // "Mac" is really an iPad.
  return /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1
}

export interface PendingOAuth {
  /** 'create' links onto the anon session (new account); 'signin' replaces it. */
  intent: 'create' | 'signin'
  provider: 'google' | 'apple'
  /** Where to land once the flow completes. */
  next: string
  /** The anonymous uid to re-home posts from, if the uid ends up changing. */
  anonUid: string | null
  /** create only: the handle to reserve for the new account. */
  handle?: string
  /** create only (onboarding): claim this tag once the account exists. */
  claim?: ClaimOwnershipParams
}

const PENDING_KEY = 'kt.pendingOAuth'
/** Set by the redirect handler when a flow fails; shown on the sign-in page. */
export const AUTH_ERROR_KEY = 'kt.authError'

export const stashPending = (p: PendingOAuth): void => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(p))
  } catch {
    /* private mode / storage disabled — redirect will still sign in, just
       without the post-return completion (rare, non-fatal). */
  }
}

export const readPending = (): PendingOAuth | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY)
    return raw ? (JSON.parse(raw) as PendingOAuth) : null
  } catch {
    return null
  }
}

export const hasPending = (): boolean => {
  try {
    return sessionStorage.getItem(PENDING_KEY) !== null
  } catch {
    return false
  }
}

export const clearPending = (): void => {
  try {
    sessionStorage.removeItem(PENDING_KEY)
  } catch {
    /* ignore */
  }
}
